import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { productIntelDashboard, productIntelAction, productIntelBulkAction, fmtMoney, fmtNum } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Package, TrendUp, TrendDown, Clock, WarningCircle, Stack, MagnifyingGlass, PencilSimple,
  FacebookLogo, InstagramLogo, EnvelopeSimple, Tag, Image, Strategy, Sparkle, ShieldCheck, CurrencyDollar, Storefront,
  Heartbeat, CaretDown, CaretUp, ArrowsClockwise, Lightning, VideoCamera, Megaphone, TiktokLogo, Gift, ChartLineUp, CheckSquare, Lightbulb,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "all", label: "All", icon: Package },
  { key: "best_seller", label: "Best Sellers", icon: TrendUp },
  { key: "slow_mover", label: "Slow Movers", icon: TrendDown },
  { key: "no_recent_sales", label: "No Recent Sales", icon: Clock },
  { key: "low_inventory", label: "Low Inventory", icon: WarningCircle },
  { key: "high_inventory", label: "High Inventory", icon: Stack },
  { key: "seo_opportunity", label: "SEO Opportunities", icon: MagnifyingGlass },
  { key: "needs_description", label: "Needs Description", icon: PencilSimple },
  { key: "promote", label: "Recommend Promotion", icon: Strategy },
  { key: "healthy", label: "Healthy", icon: Heartbeat },
  { key: "needs_attention", label: "Needs Attention", icon: WarningCircle },
  { key: "critical", label: "Critical", icon: WarningCircle },
];

const ACTION_ICON = {
  improve_seo: MagnifyingGlass, seo_meta: MagnifyingGlass, rewrite_description: PencilSimple,
  increase_price: TrendUp, decrease_price: TrendDown, facebook_post: FacebookLogo,
  instagram_post: InstagramLogo, email_campaign: EnvelopeSimple, tiktok_campaign: TiktokLogo,
  video_concept: VideoCamera, add_to_promotion: Tag, create_bundle: Gift, holiday_promotion: Gift,
  product_image: Image, lifestyle_image: Image, product_video: VideoCamera,
  meta_ads: Megaphone, google_shopping: ChartLineUp, create_campaign: Strategy,
};
const ALL_ACTIONS = [
  ["improve_seo", "Improve SEO"], ["seo_meta", "SEO Meta"], ["rewrite_description", "Rewrite Desc"],
  ["facebook_post", "Facebook"], ["instagram_post", "Instagram"], ["email_campaign", "Email"],
  ["tiktok_campaign", "TikTok"], ["create_bundle", "Bundle"], ["add_to_promotion", "Promotion"],
  ["holiday_promotion", "Holiday"], ["increase_price", "Raise Price"], ["decrease_price", "Lower Price"],
  ["product_image", "Product Image"], ["lifestyle_image", "Lifestyle"], ["product_video", "Video"],
  ["meta_ads", "Meta Ads"], ["google_shopping", "Google Shopping"], ["create_campaign", "Campaign"],
];
const BULK_OPTIONS = [
  ["rewrite_description", "Rewrite Descriptions"], ["improve_seo", "Improve SEO Titles"],
  ["seo_meta", "Generate SEO Meta Descriptions"], ["facebook_post", "Generate Facebook Posts"],
  ["instagram_post", "Generate Instagram Posts"], ["email_campaign", "Generate Email Campaigns"],
  ["create_bundle", "Create Promotional Bundles"], ["product_image", "Generate Product Images"],
  ["create_campaign", "Queue Marketing Campaigns"], ["holiday_promotion", "Mark for Holiday Promotions"],
];

const HEALTH = {
  healthy: { dot: "bg-emerald-400", text: "text-emerald-400", ring: "border-emerald-500/30", label: "Healthy" },
  needs_attention: { dot: "bg-amber-400", text: "text-amber-300", ring: "border-amber-500/30", label: "Needs Attention" },
  critical: { dot: "bg-red-500", text: "text-red-400", ring: "border-red-500/30", label: "Critical" },
};

const flagBadges = (f) => {
  const out = [];
  if (f.best_seller) out.push(["Best Seller", "bg-emerald-500/15 text-emerald-400"]);
  if (f.slow_mover) out.push(["Slow Mover", "bg-amber-500/15 text-amber-300"]);
  if (f.no_recent_sales) out.push(["No Recent Sales", "bg-red-500/15 text-red-400"]);
  if (f.low_inventory) out.push(["Low Inventory", "bg-orange-500/15 text-orange-400"]);
  if (f.high_inventory) out.push(["High Inventory", "bg-sky-500/15 text-sky-400"]);
  if (f.seo_opportunity) out.push(["SEO Opportunity", "bg-purple-500/15 text-purple-300"]);
  if (f.needs_description) out.push(["Thin Description", "bg-white/8 text-[#cfcfcf]"]);
  return out;
};

function SummaryCard({ label, value, icon: Icon, onClick, active }) {
  return (
    <button onClick={onClick} data-testid={`summary-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`card-matte p-4 text-left transition-colors ${active ? "border-[#6B3DFF]/50 bg-[#6B3DFF]/[0.06]" : "hover:border-white/20"}`}>
      <div className="flex items-center gap-2 label-caps"><Icon size={13} className="brand-text" weight="bold" /> {label}</div>
      <div className="font-head text-xl md:text-2xl font-semibold mt-2">{value}</div>
    </button>
  );
}

function HealthBar({ f }) {
  const pct = Math.round((f.score / f.max) * 100);
  const color = pct >= 60 ? "bg-emerald-400" : pct >= 35 ? "bg-amber-400" : "bg-red-500";
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-[#cfcfcf]"><span>{f.label}</span><span className="text-[#7a7a7a]">{f.score}/{f.max}</span></div>
      <div className="h-1.5 rounded-full bg-white/8 mt-1 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <div className="text-[10px] text-[#7a7a7a] mt-0.5">{f.note}</div>
    </div>
  );
}

function ProductCard({ p, onAction, pending, selected, onToggleSelect }) {
  const [open, setOpen] = useState(false);
  const recKeys = (p.recommendations || []).map((r) => r.action);
  const h = p.health || { status: "needs_attention", score: 0, breakdown: [], improvements: [], summary: "" };
  const hc = HEALTH[h.status] || HEALTH.needs_attention;
  const lastSale = p.last_sold_at ? p.last_sold_at.slice(0, 10) : (p.last_sold_days == null ? "Never" : `${p.last_sold_days}d ago`);
  return (
    <div className={`card-matte p-4 ${selected ? "border-[#6B3DFF]/50" : ""}`} data-testid={`product-card-${p.shopify_id}`}>
      <div className="flex gap-3">
        <Checkbox data-testid={`select-${p.shopify_id}`} checked={selected} onCheckedChange={() => onToggleSelect(p.shopify_id)} className="mt-1 border-white/30 data-[state=checked]:bg-[#6B3DFF] data-[state=checked]:border-[#6B3DFF]" />
        {p.image ? <img src={p.image} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" /> :
          <div className="h-14 w-14 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Package size={20} className="text-[#5a5a5a]" /></div>}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm leading-snug truncate">{p.title}</div>
          <div className="text-[11px] text-[#7a7a7a] mt-0.5 truncate">{p.type || "—"} · {p.vendor || "—"} · {fmtMoney(p.price)}</div>
          <div className="flex flex-wrap gap-1 mt-1.5">{flagBadges(p.flags).map(([t, c]) => <span key={t} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${c}`}>{t}</span>)}</div>
        </div>
        <div className={`shrink-0 text-center rounded-lg border ${hc.ring} px-2 py-1`} data-testid={`health-${p.shopify_id}`} title={hc.label}>
          <div className={`font-head text-lg font-bold ${hc.text}`}>{h.score}</div>
          <div className="flex items-center gap-1 text-[9px] text-[#7a7a7a]"><span className={`h-1.5 w-1.5 rounded-full ${hc.dot}`} /> Health</div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-3 text-center">
        {[["Units", fmtNum(p.units_sold)], ["Revenue", fmtMoney(p.revenue)], ["Stock", fmtNum(p.inventory)], ["Last Sale", lastSale]].map(([l, v]) => (
          <div key={l} className="bg-white/[0.03] rounded-lg py-1.5"><div className="text-[9px] uppercase text-[#7a7a7a]">{l}</div><div className="text-xs font-semibold text-[#cfcfcf] truncate px-0.5">{v}</div></div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[10px]">
        <span className={`px-1.5 py-0.5 rounded ${p.description_len >= 120 ? "bg-emerald-500/15 text-emerald-400" : "bg-white/8 text-[#cfcfcf]"}`}>Desc: {p.description_len >= 120 ? "OK" : "Thin"}</span>
        <span className={`px-1.5 py-0.5 rounded ${p.has_seo_title ? "bg-emerald-500/15 text-emerald-400" : "bg-purple-500/15 text-purple-300"}`}>SEO: {p.has_seo_title ? "Set" : "Missing"}</span>
        {p.tags ? <span className="px-1.5 py-0.5 rounded bg-white/8 text-[#9a9a9a] truncate max-w-[160px]">Tags: {p.tags}</span> : null}
        <span className="text-[#7a7a7a] ml-auto">{p.repeat_label}</span>
      </div>

      <button data-testid={`health-toggle-${p.shopify_id}`} onClick={() => setOpen(!open)} className="mt-3 w-full flex items-center justify-between text-[11px] text-[#9a9a9a] hover:text-white">
        <span className="flex items-center gap-1"><Heartbeat size={12} className={hc.text} /> AI Health: {hc.label} — why?</span>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`health-detail-${p.shopify_id}`}>
          <div className="text-[11px] text-[#cfcfcf]">{h.summary}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">{(h.breakdown || []).map((f) => <HealthBar key={f.label} f={f} />)}</div>
          {(h.improvements || []).length > 0 && (
            <div className="text-[11px] text-amber-300 pt-1"><span className="font-semibold">To improve:</span> {h.improvements.join(" · ")}</div>
          )}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-white/8">
        <div className="label-caps mb-1.5 flex items-center gap-1"><Sparkle size={11} className="brand-text" weight="fill" /> One-Click AI Recommendations</div>
        {(p.recommendations || []).length > 0 && (
          <div className="text-[10px] text-[#9a9a9a] mb-2 space-y-0.5">
            {(p.recommendations || []).slice(0, 3).map((r) => (
              <div key={r.action} className="flex items-start gap-1"><Lightning size={10} className="brand-text mt-0.5 shrink-0" weight="fill" /><span><span className="text-[#cfcfcf]">{r.label}:</span> {r.reason}</span></div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {ALL_ACTIONS.map(([key, label]) => {
            const Icon = ACTION_ICON[key];
            const rec = recKeys.includes(key);
            return (
              <button key={key} data-testid={`action-${key}-${p.shopify_id}`} disabled={pending === `${p.shopify_id}-${key}`}
                onClick={() => onAction(p, key)} title={label}
                className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-colors ${rec ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-[#A56CFF]" : "border-white/8 bg-white/[0.02] text-[#9a9a9a] hover:text-white hover:border-white/20"} ${pending === `${p.shopify_id}-${key}` ? "opacity-50" : ""}`}>
                <Icon size={12} weight="bold" /> {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ProductIntelligencePage() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [bucket, setBucket] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("revenue");
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selectAllCatalog, setSelectAllCatalog] = useState(false);
  const pageSize = 60;

  // debounce search
  useEffect(() => { const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350); return () => clearTimeout(t); }, [searchInput]);
  useEffect(() => { setPage(1); setSelected(new Set()); setSelectAllCatalog(false); }, [bucket, search, sort, activeId]);

  const q = useQuery({
    queryKey: ["product-intel", activeId, bucket, search, sort, page],
    queryFn: () => productIntelDashboard(activeId, { bucket, search, sort, page, page_size: pageSize }),
    enabled: !!activeId, keepPreviousData: true,
  });
  const d = q.data;
  const s = d?.summary || {};
  const products = d?.products || [];
  const totalFiltered = d?.total_filtered || 0;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));

  const toggleSelect = (id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    setSelectAllCatalog(false);
  };
  const selectPage = () => { setSelected(new Set(products.map((p) => p.shopify_id))); setSelectAllCatalog(false); };
  const clearSel = () => { setSelected(new Set()); setSelectAllCatalog(false); };

  const actionM = useMutation({
    mutationFn: ({ p, action }) => productIntelAction({ client_id: activeId, shopify_product_id: p.shopify_id, product_title: p.title, action }),
    onMutate: ({ p, action }) => setPending(`${p.shopify_id}-${action}`),
    onSettled: () => setPending(null),
    onSuccess: (r) => {
      if (r.navigate) { navigate(`/director?prompt=${encodeURIComponent(r.prompt)}`); return; }
      toast.success("Sent to Approval Queue — review & approve before anything goes live");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });
  const onAction = (p, action) => actionM.mutate({ p, action });

  const bulkM = useMutation({
    mutationFn: (action) => productIntelBulkAction(
      selectAllCatalog
        ? { client_id: activeId, action, select_all: true, bucket, search }
        : { client_id: activeId, action, product_ids: Array.from(selected) }
    ),
    onSuccess: (r) => {
      toast.success(`Queued ${r.queued} action(s) to the Approval Queue${r.capped ? ` (capped at ${r.cap})` : ""} — nothing publishes automatically`);
      clearSel();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk action failed"),
  });

  const selCount = selectAllCatalog ? totalFiltered : selected.size;
  const lastSync = d?.last_sync ? new Date(d.last_sync).toLocaleString() : "—";

  return (
    <PageContainer>
      <PageHeader eyebrow="Shopify" title="Product Intelligence"
        subtitle="Live product analytics, AI Health Scores & one-click recommendations from your synced Shopify store. Every action is approval-gated — nothing changes your store automatically.">
        <div className="flex items-center gap-3">
          <span data-testid="intel-last-sync" className="text-[11px] text-[#7a7a7a]">Last Synced: <span className="text-[#cfcfcf]">{lastSync}</span></span>
          <span data-testid="intel-data-source" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${d?.data_source === "live" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-white/5 text-[#9a9a9a] border-white/10"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${d?.data_source === "live" ? "bg-emerald-400" : "bg-[#7a7a7a]"}`} />
            {d?.data_source === "live" ? "Live Shopify data" : "No products synced"}
          </span>
        </div>
      </PageHeader>

      {/* Executive Insights */}
      {(d?.insights || []).length > 0 && (
        <div className="card-matte p-5 mb-6" data-testid="exec-insights">
          <div className="label-caps mb-3 flex items-center gap-1"><Lightbulb size={13} className="brand-text" weight="fill" /> Executive Product Insights</div>
          <div className="grid md:grid-cols-2 gap-2.5">
            {(d.insights).map((ins, i) => {
              const c = ins.severity === "high" ? "text-red-400" : ins.severity === "warn" ? "text-amber-300" : "text-emerald-400";
              return (
                <div key={i} data-testid={`insight-${i}`} className="flex items-start gap-2 text-sm">
                  <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${c.replace("text-", "bg-")}`} />
                  <span className="text-[#cfcfcf]">{ins.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6" data-testid="intel-summary">
        <SummaryCard label="Total Products" value={fmtNum(s.total_products)} icon={Package} onClick={() => setBucket("all")} active={bucket === "all"} />
        <SummaryCard label="Avg Health" value={`${s.avg_health || 0}`} icon={Heartbeat} onClick={() => {}} active={false} />
        <SummaryCard label="Healthy" value={fmtNum(s.healthy)} icon={Heartbeat} onClick={() => setBucket("healthy")} active={bucket === "healthy"} />
        <SummaryCard label="Needs Attention" value={fmtNum(s.needs_attention)} icon={WarningCircle} onClick={() => setBucket("needs_attention")} active={bucket === "needs_attention"} />
        <SummaryCard label="Critical" value={fmtNum(s.critical)} icon={WarningCircle} onClick={() => setBucket("critical")} active={bucket === "critical"} />
        <SummaryCard label="Total Revenue" value={fmtMoney(s.total_revenue)} icon={CurrencyDollar} onClick={() => setBucket("all")} active={false} />
        <SummaryCard label="Best Sellers" value={fmtNum(s.best_sellers)} icon={TrendUp} onClick={() => setBucket("best_seller")} active={bucket === "best_seller"} />
        <SummaryCard label="No Recent Sales" value={fmtNum(s.no_recent_sales)} icon={Clock} onClick={() => setBucket("no_recent_sales")} active={bucket === "no_recent_sales"} />
        <SummaryCard label="Low Inventory" value={fmtNum(s.low_inventory)} icon={WarningCircle} onClick={() => setBucket("low_inventory")} active={bucket === "low_inventory"} />
        <SummaryCard label="SEO Opportunities" value={fmtNum(s.seo_opportunities)} icon={MagnifyingGlass} onClick={() => setBucket("seo_opportunity")} active={bucket === "seo_opportunity"} />
        <SummaryCard label="Needs Description" value={fmtNum(s.needs_description)} icon={PencilSimple} onClick={() => setBucket("needs_description")} active={bucket === "needs_description"} />
        <SummaryCard label="High Inventory" value={fmtNum(s.high_inventory)} icon={Stack} onClick={() => setBucket("high_inventory")} active={bucket === "high_inventory"} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex flex-wrap gap-1.5" data-testid="intel-buckets">
          {BUCKETS.map((b) => (
            <button key={b.key} data-testid={`bucket-${b.key}`} onClick={() => setBucket(b.key)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border ${bucket === b.key ? "border-[#6B3DFF]/50 bg-[#6B3DFF]/10 text-white" : "border-white/10 text-[#9a9a9a] hover:text-white"}`}>
              <b.icon size={13} /> {b.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="intel-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#10101A] border border-white/10 rounded-lg px-2.5 py-2 text-xs h-9">
            <option value="revenue">Sort: Revenue</option>
            <option value="units">Sort: Units</option>
            <option value="health">Sort: Health</option>
            <option value="inventory">Sort: Inventory</option>
            <option value="title">Sort: Name</option>
          </select>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
            <Input data-testid="intel-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search products…" className="bg-[#10101A] border-white/10 pl-8 h-9 w-56" />
          </div>
        </div>
      </div>

      {/* Bulk AI Management bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 card-matte p-3" data-testid="bulk-bar">
        <div className="flex items-center gap-2 text-xs text-[#9a9a9a] flex-wrap">
          <CheckSquare size={15} className="brand-text" />
          <span data-testid="bulk-selection-count" className="text-[#cfcfcf] font-semibold">{selCount} selected</span>
          <button data-testid="bulk-select-page" onClick={selectPage} className="text-ember underline-offset-2 hover:underline brand-text">Select page ({products.length})</button>
          <span className="text-[#5a5a5a]">·</span>
          <button data-testid="bulk-select-all" onClick={() => { setSelectAllCatalog(true); setSelected(new Set()); }} className="brand-text hover:underline">Select entire catalog ({totalFiltered})</button>
          {selCount > 0 && <><span className="text-[#5a5a5a]">·</span><button data-testid="bulk-clear" onClick={clearSel} className="text-[#9a9a9a] hover:text-white">Clear</button></>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="bulk-action-btn" disabled={selCount === 0 || bulkM.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-9">
              <Sparkle size={15} className="mr-1.5" weight="fill" /> {bulkM.isPending ? "Queuing…" : "Bulk AI Action"} <CaretDown size={13} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 bg-[#14141F] border-white/10 text-white">
            <DropdownMenuLabel className="label-caps">Apply to {selCount} product(s)</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            {BULK_OPTIONS.map(([key, label]) => {
              const Icon = ACTION_ICON[key] || Sparkle;
              return (
                <DropdownMenuItem key={key} data-testid={`bulk-option-${key}`} onClick={() => bulkM.mutate(key)} className="cursor-pointer focus:bg-white/5">
                  <Icon size={14} className="mr-2 brand-text" /> {label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-2 mb-3 text-xs text-[#7a7a7a]">
        <ShieldCheck size={14} className="brand-text" /> Showing {products.length} of {totalFiltered} products. AI actions queue to the Approval Queue — nothing publishes automatically.
      </div>

      {q.isLoading ? (
        <div className="text-[#9a9a9a] ai-thinking py-10 text-center">Loading product intelligence…</div>
      ) : products.length === 0 ? (
        <div className="card-matte p-10 text-center" data-testid="intel-empty">
          <Package size={32} className="text-[#5a5a5a] mx-auto mb-3" />
          {d?.data_source === "live" ? (
            <div className="text-[#9a9a9a]">No products match this view for <span className="text-[#cfcfcf] font-semibold">{activeClient?.name}</span>.</div>
          ) : (
            <>
              <div className="text-[#cfcfcf] font-semibold mb-1">No Shopify products synced for {activeClient?.name}</div>
              <div className="text-sm text-[#9a9a9a] mb-4">Connect &amp; sync this brand's Shopify store to unlock live product intelligence. Each workspace shows only its own products.</div>
              <button data-testid="intel-connect-shopify" onClick={() => navigate("/shopify")} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
                <Storefront size={15} /> Connect Shopify
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="product-grid">
            {products.map((p) => (
              <ProductCard key={p.shopify_id} p={p} onAction={onAction} pending={pending}
                selected={selectAllCatalog || selected.has(p.shopify_id)} onToggleSelect={toggleSelect} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6" data-testid="intel-pagination">
              <Button data-testid="page-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-white/10 bg-transparent text-[#cfcfcf]">Previous</Button>
              <span className="text-xs text-[#9a9a9a]">Page {page} of {totalPages}</span>
              <Button data-testid="page-next" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="border-white/10 bg-transparent text-[#cfcfcf]">Next</Button>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
